import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStoreInfo } from '@/lib/store';
import { getStoreId } from '@/lib/utils';

interface ImagePosition {
  x: number;
  y: number;
  scale: number;
  rotation: number;
  delay: number;
  duration: number;
}

interface Product {
  images: string[];
}

export function MaintenancePage() {
  const { storeInfo } = useStoreInfo();
  const [positions, setPositions] = useState<ImagePosition[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [productImages, setProductImages] = useState<string[]>([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/products.php`);
        const data = await response.json();

        if (data.status && data.products) {
          const images = data.products
            .filter((product: Product) => product.images && product.images.length > 0)
            .map((product: Product) => product.images[0]);
          setProductImages(images);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    if (productImages.length === 0) return;

    const generatePositions = () => {
      return productImages.map(() => ({
        x: Math.random() * window.innerWidth,
        y: -100 - Math.random() * 500,
        scale: 0.7 + Math.random() * 0.5,
        rotation: -15 + Math.random() * 30,
        delay: Math.random() * 2,
        duration: 3 + Math.random() * 4
      }));
    };

    const updatePositions = () => {
      setPositions(generatePositions());
    };

    updatePositions();
    const interval = setInterval(updatePositions, 7000);

    return () => clearInterval(interval);
  }, [productImages.length]);

  useEffect(() => {
    if (productImages.length === 0) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [productImages.length]);

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-gray-900 to-black overflow-hidden">
      <div className="absolute inset-0">
        {positions.map((position, index) => (
          <motion.div
            key={`image-${index}`}
            initial={{ 
              x: position.x,
              y: position.y,
              scale: position.scale,
              rotate: position.rotation,
              opacity: 0 
            }}
            animate={{ 
              y: window.innerHeight + 100,
              opacity: [0, 0.2, 0.2, 0] 
            }}
            transition={{
              duration: position.duration,
              delay: position.delay,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute"
          >
            <img
              src={productImages[index % productImages.length]}
              alt="Product"
              className="w-48 h-48 object-cover rounded-lg shadow-2xl"
              style={{ filter: 'brightness(0.7)' }}
            />
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl mx-auto p-8 text-center"
        >
          <div className="bg-white/10 backdrop-blur-lg p-8 rounded-2xl shadow-2xl">
            <motion.div
              animate={{
                scale: [1, 1.02, 1],
                rotate: [0, 1, -1, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="mb-6"
            >
              <h1 className="text-5xl font-bold text-white mb-4">
                Under Maintenance
              </h1>
              <div className="w-16 h-1 bg-blue-500 mx-auto rounded-full mb-4" />
            </motion.div>
            
            <p className="text-xl text-gray-300 mb-8">
              We're making our store even better for you.<br />
              Please check back soon!
            </p>

            <div className="relative w-24 h-24 mx-auto">
              <motion.div
                animate={{
                  rotate: 360,
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute inset-0"
              >
                <div className="w-full h-full border-4 border-blue-500 border-t-transparent rounded-full" />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}